'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { setCurrentRouteName, setPageViewName } from '../core/send-action';

export interface NewRelicPageViewTrackerProps {
  /**
   * Optional function to map a raw pathname to a human-readable route name.
   * Receives the pathname (locale-stripped if applicable) and returns the
   * step name to pass to setPageViewName / setCurrentRouteName.
   *
   * IMPORTANT: return route TEMPLATES not resolved paths.
   * Use '/loans/[id]' not '/loans/123' to avoid high cardinality and PII in names.
   *
   * If omitted, the raw pathname is used as-is (not recommended for production
   * because it may contain IDs).
   */
  inferRouteName?: (pathname: string) => string | null;
  /** Suppress all tracking — useful for error pages. */
  suppress?: boolean;
}

/**
 * Client component that calls BOTH setPageViewName AND setCurrentRouteName on
 * every SPA route change so that both PageView and BrowserInteraction events
 * carry readable step names.
 *
 * - setPageViewName: names standard PageView events
 * - setCurrentRouteName: names BrowserInteraction SPA route-change events
 *   (requires Pro+SPA agent — nr-loader-spa-*.min.js)
 *
 * Deduplicates: only fires when the pathname actually changes.
 * Mount this alongside the existing GtmPageViewTracker in providers.tsx.
 */
export function NewRelicPageViewTracker({
  inferRouteName,
  suppress,
}: NewRelicPageViewTrackerProps): null {
  const pathname = usePathname();
  const lastFiredPath = useRef<string | null>(null);

  useEffect(() => {
    if (suppress) return;
    if (pathname === lastFiredPath.current) return;

    const routeName = inferRouteName
      ? inferRouteName(pathname)
      : pathname;

    if (routeName) {
      setPageViewName(routeName);
      setCurrentRouteName(routeName);
    }

    lastFiredPath.current = pathname;
  }, [pathname, suppress, inferRouteName]);

  return null;
}
